import React, { memo, useCallback, useMemo, useContext } from 'react';
import classNames from 'classnames';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { PropertyInstance, PropertyValueType, PropertyValueMap } from '../types';
import useT from '../../../translation/src/hooks/useT';
import { UserContext } from '../../../contexts/UserContext';
import { BooleanInput, IntInput, FloatInput, RangeInput, StringInput, SelectInput, ColorInput, GradientInput, MinMaxInput, JsonInput } from './inputs';
import { GetUnit } from '../utils/getUnit';

interface PropertyInputProps {
  instance: PropertyInstance;
  onChange: (value: PropertyValueMap[keyof PropertyValueMap]) => void;
  className?: string;
  disabled?: boolean;
}

/**
 * Generic parameter input component that renders the appropriate control
 * based on the parameter's value type
 */
const PropertyInput_: React.FC<PropertyInputProps> = ({ instance, onChange, className, disabled = false }) => {
  // Get user context and localized strings - hooks must be called before any conditional returns
  const currentUser = useContext(UserContext);
  const invalidConfigText = useT('Invalid parameter configuration', {});
  const resetTooltip = useT('Reset to default value', {});
  const unsupportedTypeText = useT('Unsupported parameter type', {});

  // Extract values that might be undefined
  const config = instance?.config;
  const error = instance?.error;
  const isDisabled = disabled || config?.disabled;
  // Tooltip/description are registered as English source strings — translate
  // at the rendering layer (same as PropertyGroup's label/tooltip).
  const tooltip = useT(config?.ui?.tooltip || config?.description || '', { swc: true }) || undefined;

  const handleChange = useCallback(
    (newValue: PropertyValueMap[keyof PropertyValueMap]) => {
      if (isDisabled) return;
      onChange(newValue);
    },
    [onChange, isDisabled],
  );

  const handleReset = useCallback(() => {
    if (isDisabled) return;

    if (config?.ui?.onReset) {
      // Use custom reset handler if provided
      config.ui.onReset(instance);
    } else {
      // Default reset behavior - reset to defaultValue
      const defaultValue = config?.defaultValue;
      if (defaultValue !== null) {
        handleChange(defaultValue);
      }
    }
  }, [config, instance, handleChange, isDisabled]);

  // Reset button component
  const ResetButton = useMemo(() => {
    if (!config?.ui?.showResetButton) return null;

    return (
      <ArrowPathIcon
        className={classNames('flex-shrink-0 w-5 h-5 cursor-pointer ml-2', 'text-neon-green-500 dark:text-neon-green-300', 'hover:text-neon-green-600 dark:hover:text-neon-green-400', 'transition-colors duration-200', {
          'opacity-50 cursor-not-allowed': isDisabled,
        })}
        onClick={isDisabled ? undefined : handleReset}
        title={resetTooltip}
      />
    );
  }, [config?.ui?.showResetButton, isDisabled, handleReset, resetTooltip]);

  const inputClassName = useMemo(
    () =>
      classNames(config?.className, className, {
        'border-error-red-500': error,
        'opacity-50 cursor-not-allowed': isDisabled,
      }),
    [config?.className, className, error, isDisabled],
  );

  // Safety check for invalid instance - must be after all hooks
  if (!instance || !config) {
    console.error('PropertyInput received invalid instance:', instance);
    return <div className="text-red-500 text-sm">{invalidConfigText}</div>;
  }

  const unitString = GetUnit(config.ui);

  // Render different input types based on parameter configuration
  switch (config.valueType) {
    case PropertyValueType.BOOLEAN:
      return (
        <div className="flex items-center" title={tooltip}>
          <BooleanInput instance={instance} onChange={handleChange} disabled={isDisabled} className={inputClassName} />
          {ResetButton}
        </div>
      );

    case PropertyValueType.INT:
      return (
        <div className="flex items-center" title={tooltip}>
          <IntInput instance={instance} onChange={handleChange} disabled={isDisabled} className={inputClassName} />
          {ResetButton}
        </div>
      );

    case PropertyValueType.FLOAT:
      return (
        <div className="flex items-center" title={tooltip}>
          <FloatInput instance={instance} onChange={handleChange} disabled={isDisabled} className={inputClassName} />
          {ResetButton}
        </div>
      );

    case PropertyValueType.RANGE:
      return (
        <div title={tooltip}>
          <div className="flex items-center">
            <RangeInput instance={instance} onChange={handleChange} disabled={isDisabled} className={inputClassName} />
            <div className="mb-3">{ResetButton}</div>
          </div>
        </div>
      );

    case PropertyValueType.MIN_MAX:
      return (
        <div className={classNames('flex items-center w-full', inputClassName)} title={tooltip}>
          <MinMaxInput instance={instance} onChange={(value: PropertyValueMap[keyof PropertyValueMap]) => handleChange(value)} disabled={isDisabled} />
          {unitString && <div className="mx-1 mt-3">{unitString}</div>}
          <div className="mt-4">{ResetButton}</div>
        </div>
      );

    case PropertyValueType.STRING:
      return (
        <div className="flex items-center" title={tooltip}>
          <StringInput instance={instance} onChange={handleChange} disabled={isDisabled} className={inputClassName} />
          {ResetButton}
        </div>
      );

    case PropertyValueType.SELECT:
      return (
        <div className="flex items-center w-full" title={tooltip}>
          <SelectInput instance={instance} onChange={handleChange} disabled={isDisabled} className={inputClassName} />
          {ResetButton}
        </div>
      );

    case PropertyValueType.COLOR:
      return (
        <div className="flex items-center" title={tooltip}>
          <ColorInput instance={instance} onChange={handleChange} disabled={isDisabled} className={inputClassName} />
          {ResetButton}
        </div>
      );

    case PropertyValueType.GRADIENT:
      return (
        <div className="flex items-center" title={tooltip}>
          <GradientInput instance={instance} onChange={handleChange} disabled={isDisabled} className={inputClassName} />
          {ResetButton}
        </div>
      );

    case PropertyValueType.JSON:
      // Read-only pretty-print; JSON properties are tool-written today and
      // there's no use case for free-form editing in the panel.
      return (
        <div className="flex items-start w-full" title={tooltip}>
          <JsonInput instance={instance} onChange={handleChange} disabled={isDisabled} className={inputClassName} />
        </div>
      );

    default:
      // Only show unsupported type details to superAdmin users
      if (!currentUser.isSuperAdmin) {
        return null; // Hide from regular users
      }

      return (
        <div className="text-red-500 text-sm">
          {unsupportedTypeText}: {config.valueType}
        </div>
      );
  }
};

export const PropertyInput = memo(PropertyInput_);
